import Dialog from "@components/Dialog"
import FileUploader from "@components/FileUploader"
import {
  LOCAL_SCENARIO_HASH,
  saveGraphBase64ToIndexedDb,
} from "@features/graph/state/indexedDBHelpers"
import FileContentPreviewTable from "@features/upload/FileContentPreviewTable"

import { Button, CircularProgress, Slide, Stack } from "@mui/material"
import { TransitionProps } from "@mui/material/transitions"
import {
  GraphParametersImportError,
  usePutGraphParametersMutation,
} from "@store/services/graphsService"
import { isExcelFileEmpty } from "@utils/file"
import { enqueueSnackbar } from "notistack"
import { forwardRef, useEffect, useState } from "react"

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

type ParametersUploadDialogProps = {
  disabled?: boolean
  onClose: () => void
  graphKey: string
}

const ParametersUploadDialog = ({ onClose, graphKey }: ParametersUploadDialogProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [isValidFile, setIsValidFile] = useState(false)
  const [putGraphParams, putGraphParamsStates] = usePutGraphParametersMutation()

  const isUploadButtonEnabled = file && isValidFile && !putGraphParamsStates.isLoading

  useEffect(() => {
    if (putGraphParamsStates.isError && "data" in putGraphParamsStates.error) {
      const errorResponse = putGraphParamsStates.error.data as Response

      errorResponse.json().then((data: GraphParametersImportError) => {
        const errors = data.message.human_readable_errors

        // TODO kk: improve the way errors are displayed
        enqueueSnackbar(`Failed to upload parameters: "${errors.join("; ")}"`, {
          variant: "error",
          persist: true,
        })
      })
    }

    if (putGraphParamsStates.isSuccess) {
      saveSuccess()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [putGraphParamsStates.isLoading])

  const saveSuccess = async () => {
    await saveGraphBase64ToIndexedDb(
      graphKey,
      putGraphParamsStates.data!,
      LOCAL_SCENARIO_HASH,
      true,
    )
    // make sure to close dialog after graph data is saved
    handleClose()
    enqueueSnackbar("Parameters uploaded successfully", { variant: "success" })
  }

  const handleClose = () => {
    setFile(null)
    onClose()
  }

  const handleFileValidation = async (newFile: File) => {
    const validationErrors: string[] = []

    const isEmpty = await isExcelFileEmpty(newFile)
    if (isEmpty) validationErrors.push("The file is empty")

    setIsValidFile(validationErrors.length === 0)

    return validationErrors
  }

  const handleUpload = () => {
    if (!file) return

    putGraphParams({ graphKey, file })
  }

  return (
    <Dialog
      fullScreen
      TransitionComponent={Transition}
      open
      onClose={handleClose}
      title="Import parameters"
      actionsComponent={
        <>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isUploadButtonEnabled}
            startIcon={putGraphParamsStates.isLoading && <CircularProgress size={14} />}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </>
      }
    >
      <Stack gap={2}>
        <FileUploader
          allowedFormats={[".xlsx"]}
          onUpload={setFile}
          validate={handleFileValidation}
        />

        {file && <FileContentPreviewTable file={file} />}
      </Stack>
    </Dialog>
  )
}

export default ParametersUploadDialog
